/**
 * Form plumbing for the Usage caps screen's global caps (the four `budget` fields saved through
 * PUT /v1/admin/config {budget:{...}}), its per-user caps (the default and one person's override, saved through
 * PUT /v1/admin/usage-caps) and its answer and usage limits (`limits`, saved through PUT /v1/admin/config), plus display
 * helpers. Settings › Model & key › Budget guardrails edits three of the same global caps and saves them through the same
 * helpers, so both screens refuse and check a cap the same way. No React here so the conversions can be unit-tested.
 *
 * A cap field is read strictly (readCap): a number sets the cap, a blank field (or 0) clears it, and any other text is
 * refused, named under its field, with nothing sent. Before #232 and #233 a negative or malformed value was saved as 0, or
 * left out, and either way the spending limit was gone.
 */
import { ApiError, type BudgetSettings, type UsageLimitRow, type UserCaps } from './api';

export type GlobalCapKey = keyof BudgetSettings;
export const GLOBAL_CAP_KEYS: readonly GlobalCapKey[] = ['dailyUsd', 'monthlyUsd', 'ratePerMin', 'burst'];

export interface GlobalCapField {
  key: GlobalCapKey;
  label: string;
}

export const GLOBAL_CAP_FIELDS: readonly GlobalCapField[] = [
  { key: 'dailyUsd', label: 'Daily spend cap, every app together (USD)' },
  { key: 'monthlyUsd', label: 'Monthly spend cap, every app together (USD)' },
  { key: 'ratePerMin', label: 'Requests per minute, per user' },
  { key: 'burst', label: 'Burst: requests at once, per user' },
];

/** One line under each field: what the cap does and what a blank field means. */
export function globalCapHint(key: GlobalCapKey, defaults: { ratePerMin: number; burst: number }): string {
  switch (key) {
    case 'dailyUsd':
      return 'Today (UTC), counted across every app together. Blank = no cap.';
    case 'monthlyUsd':
      return 'Last 30 days, counted across every app together. Blank = no cap.';
    case 'ratePerMin':
      return `Sustained rate for one user on one app. Blank = ${defaults.ratePerMin} (built in).`;
    case 'burst':
      return `How many requests one user may send before the per-minute rate applies. Blank = ${defaults.burst} (built in).`;
  }
}

/** The form's text values, one per global cap. */
export type GlobalCapForm = Record<GlobalCapKey, string>;
/** A refusal under each global cap field that holds something other than a cap. */
export type GlobalCapErrors = Partial<Record<GlobalCapKey, string>>;

const positive = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : undefined;

/** The most a cap may be set to: the service refuses more (gyro-svc adminSchema.ts, `cap`). */
export const CAP_MAX = 1_000_000_000;

/**
 * One cap field's text: `value` is the number to save, or null to clear the cap (a blank field, or 0, which the service
 * reads as not set). Anything else is `ok: false`, and must be refused rather than saved: a minus sign, letters, a
 * thousands separator or currency sign, "Infinity", more than CAP_MAX, or a tiny value that would read as 0 ("1e-400").
 * A field that is not text at all is refused too, so a missing field never becomes a clear.
 */
export function readCap(text: unknown): { ok: true; value: number | null } | { ok: false } {
  if (typeof text !== 'string') return { ok: false };
  const raw = text.trim();
  if (raw === '') return { ok: true, value: null };
  if (!/^(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(raw)) return { ok: false };
  const n = Number(raw);
  if (!Number.isFinite(n) || n > CAP_MAX) return { ok: false };
  if (n > 0) return { ok: true, value: n };
  // 0 clears the cap. Digits other than zeros read as 0 only when they underflowed, which is refused, not a clear.
  return /[1-9]/.test(raw.replace(/[eE].*$/, '')) ? { ok: false } : { ok: true, value: null };
}

const CAP_RULE = `enter a number from 0 to ${CAP_MAX.toLocaleString('en-US')}, in digits with at most one decimal point`;

/** The refusal under a global cap field, ending with what a blank field means there (the hint it replaces says so too). */
export function globalCapError(key: GlobalCapKey, defaults: { ratePerMin: number; burst: number }): string {
  const blank = key === 'ratePerMin' ? `the built-in ${defaults.ratePerMin}` : key === 'burst' ? `the built-in ${defaults.burst}` : 'no cap';
  return `Not saved: ${CAP_RULE}, or leave it blank for ${blank}.`;
}

/** Stored caps to form text: a set cap shows its number, anything else (absent, 0, junk) shows blank. */
export function budgetToForm(budget: BudgetSettings | undefined): GlobalCapForm {
  const form = {} as GlobalCapForm;
  for (const key of GLOBAL_CAP_KEYS) {
    const value = positive(budget?.[key]);
    form[key] = value === undefined ? '' : String(value);
  }
  return form;
}

/**
 * Form text to the PUT /v1/admin/config save. `patch` holds only the caps that differ from `loaded`, the caps the form
 * was built from: a number sets one, 0 clears it (a blank field or 0; the service reads 0 as not set). `expected` holds,
 * for each of those, the value it was built from (0 = not set); the service checks them before it writes, so a cap another
 * admin changed in the meantime is never overwritten (409 global_caps_changed), and a cap nobody touched here is never sent.
 * A field that is not a cap is named in `errors` (#232: it used to be saved as 0, clearing the cap), and then nothing may
 * be sent: `patch` and `expected` are empty.
 */
export function formToBudgetPatch(
  form: GlobalCapForm,
  loaded: BudgetSettings | undefined,
  defaults: { ratePerMin: number; burst: number },
): { patch: BudgetSettings; expected: BudgetSettings; errors: GlobalCapErrors } {
  const patch: BudgetSettings = {};
  const expected: BudgetSettings = {};
  const errors: GlobalCapErrors = {};
  for (const key of GLOBAL_CAP_KEYS) {
    const read = readCap(form[key]);
    if (!read.ok) {
      errors[key] = globalCapError(key, defaults);
      continue;
    }
    const value = read.value ?? 0;
    const before = positive(loaded?.[key]) ?? 0;
    if (value === before) continue;
    patch[key] = value;
    expected[key] = before;
  }
  return Object.keys(errors).length ? { patch: {}, expected: {}, errors } : { patch, expected, errors };
}

/** The service refused a cap save because another admin changed those caps after the screen loaded them (409). */
export const capsChangedElsewhere = (e: unknown, code: 'global_caps_changed' | 'user_caps_changed'): boolean =>
  e instanceof ApiError && e.status === 409 && e.body?.error === code;

/** The six per-person cap windows, in the order the screen shows them. */
export const USER_CAP_FIELDS: ReadonlyArray<{ key: keyof UserCaps; label: string }> = [
  { key: 'dailyUsd', label: 'Daily $' },
  { key: 'weeklyUsd', label: 'Weekly $' },
  { key: 'monthlyUsd', label: 'Monthly $' },
  { key: 'dailyTokens', label: 'Daily tokens' },
  { key: 'weeklyTokens', label: 'Weekly tokens' },
  { key: 'monthlyTokens', label: 'Monthly tokens' },
];

/** A per-person cap form's text, one per window. */
export type UserCapForm = Record<keyof UserCaps, string>;
/** A refusal under each per-person cap field that holds something other than a cap. */
export type UserCapErrors = Partial<Record<keyof UserCaps, string>>;

/** The refusal under a per-person cap field. */
export const USER_CAP_ERROR = `Not saved: ${CAP_RULE}, or leave it blank for no cap on that window.`;

/** Stored per-person caps to form text: a set cap shows its number, anything else shows blank. */
export function userCapsToForm(caps: UserCaps | null | undefined): UserCapForm {
  const form = {} as UserCapForm;
  for (const { key } of USER_CAP_FIELDS) {
    const value = positive(caps?.[key]);
    form[key] = value === undefined ? '' : String(value);
  }
  return form;
}

/**
 * The default's or one person's cap form to the caps PUT /v1/admin/usage-caps stores: each window with a number, and
 * nothing for a blank field (or 0), which leaves that window uncapped. A field that is not a cap is named in `errors`
 * (#233: it used to be left out silently, which removed that window's cap), and then nothing may be sent.
 */
export function formToUserCaps(form: UserCapForm): { caps: UserCaps; errors: UserCapErrors } {
  const caps: UserCaps = {};
  const errors: UserCapErrors = {};
  for (const { key } of USER_CAP_FIELDS) {
    const read = readCap(form[key]);
    if (!read.ok) errors[key] = USER_CAP_ERROR;
    else if (read.value !== null) caps[key] = read.value;
  }
  return Object.keys(errors).length ? { caps: {}, errors } : { caps, errors };
}

/** Keys that are set in the stored caps, for the "n of 4 caps set" summary. */
export function capsSet(budget: BudgetSettings | undefined): GlobalCapKey[] {
  return GLOBAL_CAP_KEYS.filter((key) => positive(budget?.[key]) !== undefined);
}

const formatNumber = (n: number): string => n.toLocaleString('en-US');

/** One input of the limits form: a whole limit, or one depth (or voice level) of a per-depth limit. */
export interface LimitField {
  /** `setting`, or `setting.level`. */
  id: string;
  setting: string;
  level?: string;
  /** The depth's name for a per-depth field ("Deep"), else the limit's own name. */
  label: string;
  current: number;
  release: number;
  min: number;
  max: number;
}

/** The inputs for one limit: one, or one per depth named the way people pick it. */
export function limitFields(row: UsageLimitRow, depthNames: Readonly<Record<string, string>> = {}): LimitField[] {
  const { min, max } = row.bounds;
  if (typeof row.value === 'number') {
    return [{ id: row.setting, setting: row.setting, label: row.name, current: row.value, release: typeof row.default === 'number' ? row.default : row.value, min, max }];
  }
  const release = typeof row.default === 'number' ? {} : row.default;
  return Object.entries(row.value).map(([level, current]) => ({
    id: `${row.setting}.${level}`, setting: row.setting, level, label: depthNames[level] ?? level,
    current, release: release[level] ?? current, min, max,
  }));
}

/** The form's text, by field id. */
export type LimitForm = Record<string, string>;

/** The values in force, as form text. */
export function limitsToForm(rows: readonly UsageLimitRow[], depthNames: Readonly<Record<string, string>> = {}): LimitForm {
  const form: LimitForm = {};
  for (const row of rows) for (const field of limitFields(row, depthNames)) form[field.id] = String(field.current);
  return form;
}

/**
 * Form text to the PUT /v1/admin/config {limits} patch, with only what changed. A blank field, or the release value,
 * puts that value back to its default (null). Anything else must be a whole number within the limit's bounds; a field
 * that is not is listed in `errors`, naming the limit, and the patch must not be sent.
 */
export function formToLimitsPatch(rows: readonly UsageLimitRow[], form: LimitForm, depthNames: Readonly<Record<string, string>> = {}): { patch: Record<string, unknown>; errors: string[]; invalid: string[] } {
  const patch: Record<string, unknown> = {};
  const errors: string[] = [];
  const invalid: string[] = [];
  for (const row of rows) {
    const depths: Record<string, number | null> = {};
    for (const field of limitFields(row, depthNames)) {
      // The screen shows numbers with thousands separators (32,768), so they are accepted when typed.
      const text = (form[field.id] ?? '').trim().replace(/[,\s_]/g, '');
      const n = text === '' ? field.release : /^\d+$/.test(text) ? Number(text) : Number.NaN;
      if (!Number.isInteger(n) || n < field.min || n > field.max) {
        invalid.push(field.id);
        errors.push(`${row.name}${field.level ? ` (${field.label})` : ''}: enter a whole number from ${formatNumber(field.min)} to ${formatNumber(field.max)}, or leave it blank for the release value (${formatNumber(field.release)}).`);
        continue;
      }
      if (n === field.current) continue;
      const value = n === field.release ? null : n;
      if (field.level) depths[field.level] = value;
      else patch[row.setting] = value;
    }
    if (Object.keys(depths).length) patch[row.setting] = depths;
  }
  return { patch, errors, invalid };
}

/** Every changed limit back to its release value. */
export function resetLimitsPatch(rows: readonly UsageLimitRow[]): Record<string, null> {
  return Object.fromEntries(rows.filter((row) => row.overridden).map((row) => [row.setting, null]));
}

/**
 * A limit's value for the table: `18`, or one entry per thinking depth for a per-effort limit, named the way people pick
 * it in the chat (`Fast: 32,768 · Balanced: 32,768 · … · Deep: 96,000 · Ultra: 128,000`). An effort with no name keeps
 * its own.
 */
export function formatLimitValue(value: UsageLimitRow['value'], depthNames: Readonly<Record<string, string>> = {}): string {
  if (typeof value === 'number') return formatNumber(value);
  return Object.entries(value)
    .map(([level, n]) => `${depthNames[level] ?? level}: ${formatNumber(n)}`)
    .join(' · ');
}
